import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Button } from 'components/ui/Button/Button'
import { TitleText } from 'styling/Typography'
import { BarFill, BarTrack } from 'pages/HrDashboard/hrLayout.styled'
import { DEMO_PLANS } from 'pages/Operations/Plans/plansDemoData'
import { USE_OPS_MOCKS } from 'pages/Operations/Shared/opsControl'
import { deptByKey } from 'pages/Operations/Shared/opsDepartments'
import {
  OPS_LOCATION_STATUS,
  OPS_MACHINES,
  OPS_MACHINES_DOWN,
  OPS_MAINT_BACKLOG,
  OPS_PARTS_RISK,
  OPS_PM_COMPLIANCE,
  OPS_RISKS,
  OPS_SECURITY_POSTS,
  OPS_TRIGGERS,
} from 'pages/Operations/Shared/opsControlMocks'
import { IOpsLocationStatus } from 'types/operationsControl.interface'
import { IStatusPill } from 'types/ui/statusPill.interface'
import { IDepartmentContextPanelsProps } from 'types/departmentBoard.interface'
import {
  ActionsRow,
  BucketCount,
  BucketLabel,
  ComplianceValue,
  FuelAlertText,
  FuelOkText,
  FuelSuffix,
  GapPill,
  IncidentsNote,
  PanelCaption,
  PanelFootnote,
  PanelRow,
  PartStock,
  PlanHead,
  PlanRow,
  PlanValue,
  RiskChain,
  RiskRecommendation,
  RiskRow,
  RowMain,
  RowMuted,
  RowSide,
  RowTitle,
  RowValue,
} from './DepartmentBoard.styled'

const locationStateMeta = (state: IOpsLocationStatus['state']): { label: string; tone: IStatusPill['status'] } => {
  if (state === 'clean') return { label: 'Clean', tone: 'success' }
  if (state === 'in_progress') return { label: 'In progress', tone: 'info' }
  return { label: 'Overdue', tone: 'danger' }
}

const planPct = (current: number, target: number): number => {
  if (!target) return 0
  return Math.min(100, Math.round((current / target) * 100))
}

const LOW_FUEL_PCT = 25

const LOW_FUEL_MACHINES = OPS_MACHINES.filter((m) => (m.fuelPct ?? 100) < LOW_FUEL_PCT)

const FUEL_TRIGGER = OPS_TRIGGERS.find((t) => t.name === 'Low fuel — refuel task')

const machineFuelPct = (name: string): number | undefined => OPS_MACHINES.find((m) => m.name === name)?.fuelPct

const FuelRiskPanel = () => (
  <Container>
    <ContainerHeader>
      <TitleText>Fuel risk</TitleText>
    </ContainerHeader>
    <ContainerContent>
      {LOW_FUEL_MACHINES.length ? (
        LOW_FUEL_MACHINES.map((m) => (
          <PanelRow key={m.id}>
            <FuelAlertText>
              {m.name} · {m.fuelPct}% — refuel task queued by trigger
            </FuelAlertText>
            <StatusPill status='warning'>Low fuel</StatusPill>
          </PanelRow>
        ))
      ) : (
        <FuelOkText>All machines above {LOW_FUEL_PCT}% fuel.</FuelOkText>
      )}
      {FUEL_TRIGGER && (
        <PanelFootnote>
          Trigger: {FUEL_TRIGGER.name} · {FUEL_TRIGGER.condition}
        </PanelFootnote>
      )}
    </ContainerContent>
  </Container>
)

const MaintenancePanels = () => (
  <>
    <Container>
      <ContainerHeader>
        <TitleText>Machines down</TitleText>
      </ContainerHeader>
      <ContainerContent>
        {OPS_MACHINES_DOWN.map((m) => {
          const fuelPct = machineFuelPct(m.name)
          return (
            <PanelRow key={m.id}>
              <RowMain>
                <RowTitle>
                  {m.name}
                  {fuelPct !== undefined && <FuelSuffix>{fuelPct}% fuel</FuelSuffix>}
                </RowTitle>
                <RowMuted>{m.repairStatus}</RowMuted>
              </RowMain>
              <RowSide>
                <RowValue>Down since {m.since}</RowValue>
                <RowValue>Back ~{m.estimatedReturn}</RowValue>
              </RowSide>
            </PanelRow>
          )
        })}
      </ContainerContent>
    </Container>
    <Container>
      <ContainerHeader>
        <TitleText>Parts risk</TitleText>
      </ContainerHeader>
      <ContainerContent>
        {OPS_PARTS_RISK.map((p) => (
          <PanelRow key={p.id}>
            <RowMain>
              <RowTitle>{p.part}</RowTitle>
              <RowMuted>{p.neededFor}</RowMuted>
            </RowMain>
            <PartStock $danger={p.stock < p.reorderPoint}>
              stock {p.stock} / reorder {p.reorderPoint}
            </PartStock>
          </PanelRow>
        ))}
      </ContainerContent>
    </Container>
    <Container>
      <ContainerHeader>
        <TitleText>Backlog age</TitleText>
      </ContainerHeader>
      <ContainerContent>
        {OPS_MAINT_BACKLOG.map((b) => (
          <PanelRow key={b.label}>
            <BucketLabel $danger={b.tone === 'danger'}>{b.label}</BucketLabel>
            <BucketCount $tone={b.tone}>{b.count}</BucketCount>
          </PanelRow>
        ))}
        <PanelRow>
          <RowTitle>PM compliance</RowTitle>
          <ComplianceValue $warning={OPS_PM_COMPLIANCE < 90}>{OPS_PM_COMPLIANCE}% this month</ComplianceValue>
        </PanelRow>
        <PanelCaption>3 work orders older than a week — oldest is the conveyor guard rail.</PanelCaption>
      </ContainerContent>
    </Container>
  </>
)

const CampServicesPanels = () => (
  <Container>
    <ContainerHeader>
      <TitleText>Location status</TitleText>
    </ContainerHeader>
    <ContainerContent>
      {OPS_LOCATION_STATUS.map((l) => (
        <PanelRow key={l.id}>
          <RowMain>
            <RowTitle>{l.name}</RowTitle>
            <RowMuted>Last done {l.lastDone}</RowMuted>
          </RowMain>
          <StatusPill status={locationStateMeta(l.state).tone}>{locationStateMeta(l.state).label}</StatusPill>
        </PanelRow>
      ))}
    </ContainerContent>
  </Container>
)

const SecurityPanels = () => {
  const navigate = useNavigate()
  const [confirmedPostIds, setConfirmedPostIds] = useState<number[]>([])

  const confirmArrival = (id: number, name: string) => {
    setConfirmedPostIds((prev) => [...prev, id])
    toast.success(`${name} confirmed — coverage gap cleared`)
  }

  return (
    <Container>
      <ContainerHeader>
        <TitleText>Post coverage</TitleText>
      </ContainerHeader>
      <ContainerContent>
        {OPS_SECURITY_POSTS.map((p) => {
          const confirmed = confirmedPostIds.includes(p.id)
          return (
            <PanelRow key={p.id}>
              <RowMain>
                <RowTitle>{p.name}</RowTitle>
                <RowMuted>
                  {p.assignee ?? '—'} · shift ends in {p.shiftEndsIn}
                </RowMuted>
              </RowMain>
              {confirmed && <StatusPill status='success'>✓ Covered — confirmed 07:56</StatusPill>}
              {!confirmed && p.state === 'covered' && <StatusPill status='success'>Covered</StatusPill>}
              {!confirmed && p.state === 'handover_due' && (
                <RowSide>
                  <StatusPill status='warning'>Handover due</StatusPill>
                  <Button variant='secondary' size='sm' onClick={() => confirmArrival(p.id, p.name)}>
                    Confirm arrival
                  </Button>
                </RowSide>
              )}
              {!confirmed && p.state === 'coverage_gap' && (
                <RowSide>
                  <GapPill>COVERAGE GAP</GapPill>
                  <ActionsRow>
                    <Button variant='secondary' size='sm' onClick={() => confirmArrival(p.id, p.name)}>
                      Confirm arrival
                    </Button>
                    <Button
                      variant='danger'
                      size='sm'
                      onClick={() => toast.success(`Coverage reassignment queued for ${p.name}`)}
                    >
                      Reassign now
                    </Button>
                  </ActionsRow>
                </RowSide>
              )}
            </PanelRow>
          )
        })}
        <IncidentsNote>
          <RowMuted>Report or review security incidents</RowMuted>
          <Button variant='black' size='sm' onClick={() => navigate('/operations/safety/incidents')}>
            Incidents
          </Button>
        </IncidentsNote>
        <PanelFootnote>Guards will confirm from the mobile app — this is the supervisor override.</PanelFootnote>
      </ContainerContent>
    </Container>
  )
}

const MiningPanels = () => (
  <>
    {USE_OPS_MOCKS && (
      <Container>
        <ContainerHeader>
          <TitleText>Plan progress</TitleText>
        </ContainerHeader>
        <ContainerContent>
          {DEMO_PLANS.map((p) => (
            <PlanRow key={p.id}>
              <PlanHead>
                <RowTitle>{p.name}</RowTitle>
                <PlanValue>
                  {p.currentValue} / {p.targetValue} {p.targetUnit.name}
                </PlanValue>
              </PlanHead>
              <BarTrack>
                <BarFill $pct={planPct(p.currentValue, p.targetValue)} />
              </BarTrack>
            </PlanRow>
          ))}
        </ContainerContent>
      </Container>
    )}
    <FuelRiskPanel />
    <Container>
      <ContainerHeader>
        <TitleText>Risks</TitleText>
      </ContainerHeader>
      <ContainerContent>
        {OPS_RISKS.map((r) => (
          <RiskRow key={r.id}>
            <RiskChain>{r.chain.join(' → ')}</RiskChain>
            <RiskRecommendation>{r.recommendation}</RiskRecommendation>
          </RiskRow>
        ))}
      </ContainerContent>
    </Container>
  </>
)

const GenericPanels = ({ title }: { title: string }) => (
  <Container>
    <ContainerHeader>
      <TitleText>{title}</TitleText>
    </ContainerHeader>
    <ContainerContent>
      {USE_OPS_MOCKS &&
        DEMO_PLANS.map((p) => (
          <PlanRow key={p.id}>
            <PlanHead>
              <RowTitle>{p.name}</RowTitle>
              <PlanValue>
                {p.currentValue} / {p.targetValue} {p.targetUnit.name}
              </PlanValue>
            </PlanHead>
            <BarTrack>
              <BarFill $pct={planPct(p.currentValue, p.targetValue)} />
            </BarTrack>
          </PlanRow>
        ))}
      <RowMuted>Panels for this department are driven by the HR department registry.</RowMuted>
    </ContainerContent>
  </Container>
)

export const DepartmentContextPanels = ({ department }: IDepartmentContextPanelsProps) => {
  const template = deptByKey(department)?.kpiTemplate
  if (template === 'maintenance') return <MaintenancePanels />
  if (template === 'camp') return <CampServicesPanels />
  if (template === 'security') return <SecurityPanels />
  if (template === 'mining') return <MiningPanels />
  if (template === 'plant') return <GenericPanels title='Feed & throughput' />
  return <GenericPanels title='Plan progress' />
}
